import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Spin } from 'antd';
import Column from '../../helpers/columns';
import useDatasource from '../../hooks/useDatasource';
import {
  findAll,
  findSessionTransactions,
  findControllerSessionTransactions,
} from '../../services/validators/vw-inspector-transactions';

const ACTIONS_DICT = {
  content: [
    { id: 'TO_INSPECTION' },
    { id: 'TO_COMMERCIAL' },
    { id: 'IDENTIFYING_INSPECTOR' },
    { id: 'VERIFYING_CARD' },
  ],
  loading: false,
  error: false,
};

const InspectorActiveSessionList = () => {
  const { t } = useTranslation();

  // Level 1 expansion tracking
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  // Level 2 expansion tracking (keyed by parent id)
  const [expandedSessionKeys, setExpandedSessionKeys] = useState({});

  // State for storing fetched session data
  const [sessionTransactionsMap, setSessionTransactionsMap] = useState({});
  const [loadingSessions, setLoadingSessions] = useState({});

  // State for storing level 3 details
  const [sessionDetailsMap, setSessionDetailsMap] = useState({});
  const [loadingSessionDetails, setLoadingSessionDetails] = useState({});

  const fetcher = useCallback(
    (data) =>
      findAll({
        ...data,
        criterias: {
          ...data.criterias,
          sessionPrevIdNull: true,
        },
      }),
    [],
  );

  const { pagination, content, handleChange } = useDatasource(fetcher);

  const columns = useMemo(
    () => [
      {
        ...Column.date(
          'transactionDay',
          t('components.inspectors.transactions.transactionDay'),
          { filter: true, format: 'YYYY-MM-DD' },
        ),
        ellipsis: true,
      },
      {
        ...Column.date(
          'transactionTime',
          t('components.inspectors.transactions.transactionTime'),
          { filter: true },
        ),
        ellipsis: true,
      },
      Column.text('plateNo', t('components.inspectors.transactions.plateNo'), {
        filter: true,
      }),
      Column.text(
        'vehicleId',
        t('components.inspectors.transactions.vehicleId'),
        {
          filter: true,
        },
      ),
      Column.text('routeId', t('components.inspectors.transactions.routeId'), {
        filter: true,
      }),
      Column.bool(
        'direction',
        t('components.inspectors.transactions.direction'),
        {
          filter: true,
          labels: [
            t('table.filter.all'),
            t('components.inspectors.transactions.directionsDict.true'),
            t('components.inspectors.transactions.directionsDict.false'),
            t('table.filter.unknown'),
          ],
        },
      ),
      Column.text('id', t('components.inspectors.transactions.id'), {
        filter: true,
      }),
    ],
    [t],
  );

  // Level 2 session columns
  const sessionColumns = useMemo(
    () => [
      Column.text(
        'inspectorId',
        t('components.inspectors.transactions.employeeId'),
        {
          filter: false,
          sort: false,
        },
      ),
      Column.text(
        'fullname',
        t('components.inspectors.transactions.controlor'),
        {
          filter: false,
          sort: false,
        },
      ),
      Column.date(
        'sessionFrom',
        t('components.inspectors.transactions.sessionFrom'),
        {
          filter: false,
          sort: false,
        },
      ),
      Column.text(
        'stationFrom',
        t('components.inspectors.transactions.stationFrom'),
        {
          filter: false,
          sort: false,
        },
      ),
      Column.date(
        'sessionTo',
        t('components.inspectors.transactions.sessionTo'),
        {
          filter: false,
          sort: false,
        },
      ),
      Column.text(
        'stationTo',
        t('components.inspectors.transactions.stationTo'),
        {
          filter: false,
          sort: false,
        },
      ),
      Column.text(
        'verifiedCount',
        t('components.inspectors.transactions.verifiedCount'),
        {
          filter: false,
          sort: false,
        },
      ),
      Column.text(
        'validCount',
        t('components.inspectors.transactions.validCount'),
        {
          filter: false,
          sort: false,
        },
      ),
      Column.text(
        'totalPassengerCount',
        t('components.inspectors.transactions.totalPassengerCount'),
        {
          filter: false,
          sort: false,
        },
      ),
      Column.text(
        'invalidCount',
        t('components.inspectors.transactions.invalidCount'),
        {
          filter: false,
          sort: false,
        },
      ),
    ],
    [t],
  );

  // Level 3 details columns
  const detailsColumns = useMemo(
    () => [
      Column.text('id', 'ID', {
        filter: false,
        sort: false,
      }),
      Column.text(
        'inspectorId',
        t('components.inspectors.transactions.employeeId'),
        {
          filter: false,
          sort: false,
        },
      ),
      Column.text(
        'fullname',
        t('components.inspectors.transactions.controlor'),
        {
          filter: false,
          sort: false,
        },
      ),
      Column.dictionary(
        'action',
        t('components.inspectors.transactions.action'),
        ACTIONS_DICT,
        {
          filter: false,
          sort: false,
          dictLabel: (entry) =>
            t(`components.inspectors.transactions.actionsDict.${entry.id}`),
        },
      ),
      Column.date(
        'transactionTime',
        t('components.inspectors.transactions.transactionTime'),
        {
          filter: false,
          sort: false,
        },
      ),
      Column.text(
        'passengerCounter',
        t('components.inspectors.transactions.passengerCounter'),
        {
          filter: false,
          sort: false,
        },
      ),
      Column.text(
        'vehicleId',
        t('components.inspectors.transactions.vehicleId'),
        {
          filter: false,
          sort: false,
        },
      ),
      Column.text('routeId', t('components.inspectors.transactions.routeId'), {
        filter: false,
        sort: false,
      }),
      Column.bool('direction', t('components.travelerValidations.direction'), {
        filter: false,
        sort: false,
        labels: [
          t('table.filter.all'),
          t('components.inspectors.transactions.directionsDict.true'),
          t('components.inspectors.transactions.directionsDict.false'),
          t('table.filter.unknown'),
        ],
      }),
      Column.text(
        'validatorPosition',
        t('components.inspectors.transactions.validatorPosition'),
        {
          filter: false,
          sort: false,
        },
      ),
      Column.text('userId', t('components.inspectors.transactions.userId'), {
        filter: false,
        sort: false,
      }),
    ],
    [t],
  );

  // Level 1 expand handler
  const handleExpand = async (expanded, record) => {
    const recordId = record.id;

    if (expanded) {
      setExpandedRowKeys((prev) => [...prev, recordId]);

      // setKeys((prev) => ({ ...prev, [record.inspectorId]: recordId }));

      if (!sessionTransactionsMap[recordId]) {
        // Start loading
        setLoadingSessions((prev) => ({ ...prev, [recordId]: true }));

        try {
          // Fetch session transactions from API
          const sessionData = (await findSessionTransactions(recordId)).map(
            (item) => ({
              ...item,
              sessionId: recordId, // add sessionId for later reference
            }),
          );

          setSessionTransactionsMap((prev) => ({
            ...prev,
            [recordId]: sessionData,
          }));
        } catch (error) {
          console.error('Error fetching session transactions:', error);
          // Handle error case
        } finally {
          setLoadingSessions((prev) => ({
            ...prev,
            [recordId]: false,
          }));
        }
      }
    } else {
      setExpandedRowKeys((prev) => prev.filter((key) => key !== recordId));
    }
  };

  // Level 2 expand handler
  const handleSessionExpand = async (expanded, record, parentId) => {
    const { sessionId, inspectorId } = record;

    // Create a unique key combining parent id and inspector id
    const uniqueKey = `${parentId}_${inspectorId}`;

    if (expanded) {
      setExpandedSessionKeys((prev) => ({
        ...prev,
        [parentId]: [...(prev[parentId] || []), uniqueKey],
      }));

      if (!sessionDetailsMap[uniqueKey]) {
        // Start loading
        setLoadingSessionDetails((prev) => ({ ...prev, [uniqueKey]: true }));

        try {
          // Fetch controller session transactions from API
          const detailsData = await findControllerSessionTransactions(
            sessionId,
            inspectorId,
          );
          setSessionDetailsMap((prev) => ({
            ...prev,
            [uniqueKey]: detailsData,
          }));
        } catch (error) {
          console.error(
            'Error fetching controller session transactions:',
            error,
          );
          // Handle error case by providing empty data
          setSessionDetailsMap((prev) => ({
            ...prev,
            [uniqueKey]: [],
          }));
        } finally {
          setLoadingSessionDetails((prev) => ({
            ...prev,
            [uniqueKey]: false,
          }));
        }
      }
    } else {
      setExpandedSessionKeys((prev) => ({
        ...prev,
        [parentId]: (prev[parentId] || []).filter((key) => key !== uniqueKey),
      }));
    }
  };

  return (
    <>
      <Table
        rowKey="id"
        columns={columns}
        pagination={pagination}
        dataSource={content}
        onChange={handleChange}
        scroll={{ x: 700, y: 700 }}
        size="small"
        expandable={{
          expandedRowKeys,
          onExpand: handleExpand,
          expandedRowRender: (record) => {
            const { id } = record;
            const isLoading = loadingSessions[id];

            if (isLoading) {
              return (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Spin />
                </div>
              );
            }

            return (
              <Table
                rowKey={(row) => `${id}_${row.inspectorId}`}
                columns={sessionColumns}
                dataSource={sessionTransactionsMap[id] || []}
                pagination={false}
                size="small"
                expandable={{
                  expandedRowKeys: expandedSessionKeys[id] || [],
                  onExpand: (expanded, recordS) =>
                    handleSessionExpand(expanded, recordS, id),
                  expandedRowRender: ({ inspectorId }) => {
                    const uniqueKey = `${id}_${inspectorId}`;
                    const isLoadingDetails = loadingSessionDetails[uniqueKey];

                    if (isLoadingDetails) {
                      return (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <Spin />
                        </div>
                      );
                    }

                    return (
                      <Table
                        rowKey="id"
                        columns={detailsColumns}
                        dataSource={sessionDetailsMap[uniqueKey] || []}
                        pagination={false}
                        size="small"
                      />
                    );
                  },
                  expandRowByClick: true,
                }}
              />
            );
          },
          expandRowByClick: true,
        }}
      />
    </>
  );
};

export default InspectorActiveSessionList;
